import { useQuery } from '@tanstack/react-query';

import { fetchArtworkSettings } from '@dfq/shared/api/settings';

export interface UseArtworkUploadLimitResult {
  /** `null` while loading, on fetch failure, or if the response is malformed — no client-side cap known. */
  maxUploadSizeBytes: number | null;
  /** Decimal-MB projection of {@link maxUploadSizeBytes}, or `null` under the same conditions. */
  maxUploadSizeMb: number | null;
  /**
   * SF-CONTOUR-105 — file size, in bytes, above which the storefront must
   * skip client-side decoding entirely (no local thumbnail, no in-browser
   * auto-contour pipeline) and show a placeholder instead, relying on the
   * server-side contour preview. `null` while loading or on fetch failure —
   * callers should fail OPEN (do not gate decoding) in that case, since the
   * platform has no opinion yet.
   */
  browserDecodeMaxSizeBytes: number | null;
  /** True once the fetch has settled into an error state (e.g. network error, 500). */
  isError: boolean;
}

/**
 * Global artwork upload size limit (PL-RULES-105) — `GET /api/v1/settings/artwork`.
 *
 * Containers use this instead of hardcoding a byte/MB cap so the storefront
 * and console upload zones always reflect the admin-configured value. A
 * per-product-type cap may still apply on top of this when it is lower (see
 * `useArtworkValidation`'s spec fetch); this hook only covers the global
 * fallback limit shown when no product-specific rule is in play.
 *
 * Returns `null` (no client-side cap) rather than a hardcoded byte guess
 * when the fetch fails — the platform is the source of truth and always
 * re-validates the upload server-side regardless of what the client skips.
 * `FileUploader` and other consumers already treat a `null`/`undefined`
 * `maxFileSizeMb` as "don't enforce a client-side limit."
 */
export function useArtworkUploadLimit(): UseArtworkUploadLimitResult {
  const { data, isError } = useQuery({
    queryKey: ['settings', 'artwork'],
    queryFn: fetchArtworkSettings,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const maxUploadSizeBytes = data?.maxUploadSizeBytes ?? null;

  return {
    maxUploadSizeBytes,
    maxUploadSizeMb: maxUploadSizeBytes === null ? null : Math.floor(maxUploadSizeBytes / (1000 * 1000)),
    browserDecodeMaxSizeBytes: data?.browserDecodeMaxSizeBytes ?? null,
    isError,
  };
}
